import * as Select from '@radix-ui/react-select';
import { CountryCode, getCountryCallingCode } from 'libphonenumber-js';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { coreTokens } from '../../../core';
import { useText } from '../../../utils/hooks/use_text';
import { HeadlessButton } from '../../button';
import { theme as commonTheme } from '../../common.theme';
import { Flag, FlagType } from '../../flag';
import { Icon } from '../../icon';
import { VisuallyHidden } from '../../visually_hidden';
import { InputSelect } from '../input_select';
import { CellulaInputSelectContent } from '../input_select/_input_select_content.component';
import { elements as selectElements } from '../input_select/_input_select_root.component';
import { theme } from './input_phone_number.theme';
import { SUPPORTED_COUNTRIES } from './supported_country_list';

const elements = {
	trigger: styled(Select.Trigger)`
		all: unset;
		box-sizing: border-box;
		cursor: default;
		display: flex;
		width: 100%;
		height: 100%;
		display: flex;
		align-items: center;
		padding: ${coreTokens.spacing.x1};
		gap: ${coreTokens.spacing.x0_5};
		border-radius: ${coreTokens.borderRadius.small};

		&:hover {
			background: ${theme.getValue('countrySelectTriggerHoverBg')};
		}

		&:active,
		&[data-state='open'] {
			background: ${theme.getValue('countrySelectTriggerActiveBg')};
		}

		&:focus-visible {
			outline: 2px solid ${commonTheme.getValue('focusColor')};
		}

		&[data-disabled] {
			&:hover,
			&:active {
				background: none;
			}
		}
	`,
	value: styled(Select.Value)`
		width: ${coreTokens.size.x3};
		flex-grow: 0;
		flex-shrink: 0;
	`,
	item: styled(Select.Item)``,
};

interface InputPhoneNumberCountrySelectProps {
	onValueChange?: (countryCode: CountryCode) => void;
	value?: CountryCode;
	readOnly?: boolean;
}

export function InputPhoneNumberCountrySelect({ onValueChange, value, readOnly }: InputPhoneNumberCountrySelectProps): JSX.Element {
	const [selectedCountryCode, setSelectedCountryCode] = useState(value);
	const staticTexts = useText();
	const label = staticTexts('Component.InputPhoneNumber.CountrySelectLabel');
	const selectedCountryName = SUPPORTED_COUNTRIES.find((country) => country.countryCode === selectedCountryCode)?.nativeName;

	useEffect(
		function UpdateCountryCodeWhenValueChanges() {
			setSelectedCountryCode(value);
		},
		[value],
	);

	function handleValueChange(countryCode: CountryCode): void {
		setSelectedCountryCode(countryCode);
		onValueChange?.(countryCode);
	}

	return (
		<Select.Root onValueChange={handleValueChange} value={selectedCountryCode} disabled={readOnly}>
			<elements.trigger asChild aria-label={label}>
				<HeadlessButton>
					<elements.value className='SelectValue' placeholder={<Icon type='network' size='medium' />}>
						{selectedCountryCode ? <Flag flag={selectedCountryCode.toLocaleLowerCase() as FlagType} size='medium' /> : null}
						<VisuallyHidden>{selectedCountryName}</VisuallyHidden>
					</elements.value>
					<Select.Icon className='SelectIcon'>
						<Icon type='chevronDown' size='small' />
					</Select.Icon>
				</HeadlessButton>
			</elements.trigger>

			<CellulaInputSelectContent labelProps={{ children: label }}>
				<selectElements.scrollUpButton>
					<Icon type='chevronUp' />
				</selectElements.scrollUpButton>
				<selectElements.viewport>
					{SUPPORTED_COUNTRIES.map((country) => {
						return (
							<InputSelect.Item value={country.countryCode} key={country.countryCode}>
								{country.nativeName} (+{getCountryCallingCode(country.countryCode)})
							</InputSelect.Item>
						);
					})}
				</selectElements.viewport>
				<selectElements.scrollDownButton>
					<Icon type='chevronDown' />
				</selectElements.scrollDownButton>
			</CellulaInputSelectContent>
		</Select.Root>
	);
}
